import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// DONKEY STACKER — TOS easter egg, Donkey Kong-style platformer.
// A gantry crane up top has gone rogue and is dropping containers down the
// scaffolding. You're a yard worker on the bottom platform. Climb the
// service ladders, dodge the rolling containers, and reach the top before
// shift's end.

const W = 540
const H = 720

const PLATFORM_SLOPE = 14
const PLATFORM_LEFT_PAD = 30
const PLATFORM_RIGHT_PAD = 30
const PLATFORM_HEIGHT = 8
const PLATFORM_SPACING = 100
const PLATFORM_BOTTOM_Y = H - 50
const PLATFORM_COUNT = 6

const LADDER_W = 14

const WORKER_W = 16
const WORKER_H = 26
const WALK_SPEED = 2.2
const CLIMB_SPEED = 1.8
const JUMP_VELOCITY = -7.5
const GRAVITY = 0.42
const MAX_FALL_SPEED = 12

const CONTAINER_W = 22
const CONTAINER_H = 16
const CONTAINER_ROLL_SPEED = 1.8
const CONTAINER_SPAWN_INTERVAL_MIN = 60
const CONTAINER_SPAWN_INTERVAL_MAX = 120

const CRANE_X = W / 2 - 60
const CRANE_Y = 50

const STACKER_X = W / 2 + 70

const POWERUP_DURATION_FRAMES = 60 * 5

// Static hazards on middle platforms — broken steam pipes that periodically
// blast vertical jets. Touching one (when active) kills the worker.
// Placement is away from ladder x's so the player can't trivially route around.
const STEAM_ON_FRAMES = 180
const STEAM_OFF_FRAMES = 120
const STEAM_HEIGHT = 36
const STEAM_WIDTH = 14

const SCORE_SMASH = 300
const SCORE_REACH_TOP = 5000

const COMPANY_NAME = 'TERMINAL OS'

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext }

interface Platform {
  i: number
  x1: number
  y1: number
  x2: number
  y2: number
  rollDir: number
}

interface Ladder {
  x: number
  yTop: number
  yBottom: number
}

interface SteamJet {
  platformIdx: number
  x: number
  phaseOffset: number
  surfaceY: number
  wasActive?: boolean
}

type WorkerState = 'walking' | 'jumping' | 'climbing' | 'dead'

interface Worker {
  x: number
  y: number
  vx: number
  vy: number
  state: WorkerState
  facing: number
  onPlatform: Platform | null
  walkAnim: number
  climbAnim: number
  powerupFrames: number
  deathFrames: number
}

interface ContainerEntity {
  x: number
  y: number
  vx: number
  vy: number
  rollingOn: Platform | null
  lastPlatform: Platform | null
  smashed: boolean
  smashFrames: number
  colorIdx: number
  rotation: number
  speedMult: number
  dropAtLadders: Set<Ladder>
  passedLadders: Set<Ladder>
}

interface Powerup {
  x: number
  y: number
  life: number
}

interface Crane {
  armSwing: number
  dropFlash: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

type GameStatus = 'ready' | 'playing' | 'paused' | 'gameover' | 'won'

interface GameState {
  platforms: Platform[]
  ladders: Ladder[]
  steamJets: SteamJet[]
  worker: Worker
  containers: ContainerEntity[]
  powerup: Powerup | null
  crane: Crane
  spawnTimer: number
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  time: number
  floaters: Floater[]
  particles: Particle[]
  shake: number
  winFrames: number
}

interface HudState {
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  muted: boolean
}

interface DonkeyStackerProps {
  onClose?: () => void
}

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function randInt(min: number, max: number): number {
  return Math.floor(rand(min, max + 1))
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init(): void {
    if (this.ctx) return
    try {
      const w = window as WindowWithWebkitAudio
      const Ctor = window.AudioContext || w.webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // Audio is non-essential; ignore unavailable AudioContext.
    }
  }

  private envelope(node: GainNode, attack: number, decay: number, peak = 1): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }

  jump(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(220, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(660, this.ctx.currentTime + 0.12)
    this.envelope(gain, 0.001, 0.13, 0.12)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.14)
  }

  climb(toggle: boolean): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = toggle ? 330 : 280
    this.envelope(gain, 0.001, 0.04, 0.06)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.05)
  }

  smash(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.2
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 800
    const gain = this.ctx.createGain()
    this.envelope(gain, 0.005, dur, 0.4)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
    const osc = this.ctx.createOscillator()
    const g2 = this.ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(880, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(220, this.ctx.currentTime + 0.15)
    this.envelope(g2, 0.001, 0.16, 0.18)
    osc.connect(g2).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.18)
  }

  hurt(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + 0.6)
    this.envelope(gain, 0.005, 0.6, 0.3)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.65)
  }

  drop(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(110, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(55, this.ctx.currentTime + 0.18)
    this.envelope(gain, 0.005, 0.2, 0.15)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.22)
  }

  steam(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.25
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'highpass'
    filter.frequency.value = 2200
    const gain = this.ctx.createGain()
    this.envelope(gain, 0.005, dur, 0.15)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
  }

  powerup(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[440, 587, 740, 880].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.06
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.16)
    })
  }

  win(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[523, 659, 784, 1047, 1318].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.1
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.2, t + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.32)
    })
  }

  toggleMute(): void {
    this.muted = !this.muted
  }
}

function buildLevel(): {
  platforms: Platform[]
  ladders: Ladder[]
  steamJets: SteamJet[]
} {
  const platforms: Platform[] = []
  for (let i = 0; i < PLATFORM_COUNT; i++) {
    const y = PLATFORM_BOTTOM_Y - i * PLATFORM_SPACING
    let leftY: number
    let rightY: number
    if (i === PLATFORM_COUNT - 1) {
      leftY = y
      rightY = y
    } else if (i % 2 === 0) {
      leftY = y - PLATFORM_SLOPE / 2
      rightY = y + PLATFORM_SLOPE / 2
    } else {
      leftY = y + PLATFORM_SLOPE / 2
      rightY = y - PLATFORM_SLOPE / 2
    }
    platforms.push({
      i,
      x1: PLATFORM_LEFT_PAD,
      y1: leftY,
      x2: W - PLATFORM_RIGHT_PAD,
      y2: rightY,
      rollDir: i === PLATFORM_COUNT - 1 ? 0 : i % 2 === 0 ? 1 : -1,
    })
  }

  // Each ladder must have a UNIQUE x. If two ladders share an x, ladderAtX
  // returns the wrong one and climbing breaks for the higher pair.
  const ladders: Ladder[] = []
  const ladderXs = [W - 100, 80, W - 60, 160, W - 130]
  for (let i = 0; i < PLATFORM_COUNT - 1; i++) {
    const x = ladderXs[i]
    const lower = platforms[i]
    const upper = platforms[i + 1]
    const lowerY = interpY(lower, x)
    const upperY = interpY(upper, x)
    ladders.push({ x, yTop: upperY, yBottom: lowerY })
  }

  const steamJets: SteamJet[] = [
    { platformIdx: 1, x: 270, phaseOffset: 0 },
    { platformIdx: 2, x: 350, phaseOffset: 90 },
    { platformIdx: 3, x: 240, phaseOffset: 180 },
  ].map(j => ({
    ...j,
    surfaceY: interpY(platforms[j.platformIdx], j.x),
  }))

  return { platforms, ladders, steamJets }
}

function interpY(platform: Platform, x: number): number {
  const t = clamp((x - platform.x1) / (platform.x2 - platform.x1), 0, 1)
  return platform.y1 + (platform.y2 - platform.y1) * t
}

function platformAtFeet(
  platforms: Platform[],
  x: number,
  y: number,
  tolerance = 6,
): Platform | null {
  let best: Platform | null = null
  let bestY = Infinity
  for (const p of platforms) {
    if (x < p.x1 || x > p.x2) continue
    const py = interpY(p, x)
    if (Math.abs(y - py) <= tolerance && py < bestY) {
      best = p
      bestY = py
    }
  }
  return best
}

function ladderAtX(
  ladders: Ladder[],
  x: number,
  y: number | null = null,
  tolerance = LADDER_W / 2 + 4,
): Ladder | null {
  let best: Ladder | null = null
  let bestDist = Infinity
  for (const l of ladders) {
    if (Math.abs(x - l.x) > tolerance) continue
    if (y === null) return l
    const dTop = Math.abs(y - l.yTop)
    const dBottom = Math.abs(y - l.yBottom)
    const d = Math.min(dTop, dBottom)
    if (d < bestDist) {
      bestDist = d
      best = l
    }
  }
  return best
}

function steamJetActive(jet: SteamJet, time: number): boolean {
  const cycle = STEAM_ON_FRAMES + STEAM_OFF_FRAMES
  const t = (((time + jet.phaseOffset) % cycle) + cycle) % cycle
  return t < STEAM_ON_FRAMES
}

function steamJetPhase(jet: SteamJet, time: number): number {
  const cycle = STEAM_ON_FRAMES + STEAM_OFF_FRAMES
  const t = (((time + jet.phaseOffset) % cycle) + cycle) % cycle
  if (t < STEAM_ON_FRAMES) return t / STEAM_ON_FRAMES
  return (t - STEAM_ON_FRAMES) / STEAM_OFF_FRAMES
}

const DonkeyStacker: FC<DonkeyStackerProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    lives: 3,
    level: 1,
    status: 'ready',
    muted: false,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('donkeyStackerHi') || '0', 10) || 0
    } catch {
      // localStorage may be unavailable; ignore.
    }

    const initState = (): GameState => {
      const level = buildLevel()
      const startPlatform = level.platforms[0]
      const startX = PLATFORM_LEFT_PAD + 40
      const startY = interpY(startPlatform, startX)
      return {
        platforms: level.platforms,
        ladders: level.ladders,
        steamJets: level.steamJets,
        worker: {
          x: startX,
          y: startY,
          vx: 0,
          vy: 0,
          state: 'walking',
          facing: 1,
          onPlatform: startPlatform,
          walkAnim: 0,
          climbAnim: 0,
          powerupFrames: 0,
          deathFrames: 0,
        },
        containers: [],
        powerup: null,
        crane: { armSwing: 0, dropFlash: 0 },
        spawnTimer: 60,
        score: 0,
        hi: savedHi,
        lives: 3,
        level: 1,
        status: 'ready',
        time: 0,
        floaters: [],
        particles: [],
        shake: 0,
        winFrames: 0,
      }
    }

    stateRef.current = initState()

    const startGame = (): void => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud({
        score: 0,
        hi: s.hi,
        lives: 3,
        level: 1,
        status: 'playing',
        muted: audio.muted,
      })
    }
    startGameRef.current = startGame

    const respawnAfterDeath = (): void => {
      const s = stateRef.current
      if (!s) return
      const startPlatform = s.platforms[0]
      const startX = PLATFORM_LEFT_PAD + 40
      const startY = interpY(startPlatform, startX)
      s.worker.x = startX
      s.worker.y = startY
      s.worker.vx = 0
      s.worker.vy = 0
      s.worker.state = 'walking'
      s.worker.facing = 1
      s.worker.onPlatform = startPlatform
      s.worker.deathFrames = 0
      s.worker.powerupFrames = 0
      s.containers = []
      s.spawnTimer = 60
    }

    const advanceLevel = (): void => {
      const s = stateRef.current
      if (!s) return
      const newLevel = s.level + 1
      const carryScore = s.score
      const carryLives = s.lives
      const carryHi = s.hi
      const fresh = initState()
      Object.assign(s, fresh)
      s.level = newLevel
      s.score = carryScore
      s.lives = carryLives
      s.hi = carryHi
      s.status = 'playing'
      setHud(p => ({ ...p, level: s.level, status: 'playing' }))
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      audio.init()
      keysRef.current[e.key] = true
      keysRef.current[e.key.toLowerCase()] = true
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault()
      }
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready' && (e.key === ' ' || /^Arrow/.test(e.key))) {
        startGame()
        return
      }
      if (s.status === 'playing' && s.worker.state !== 'dead') {
        if (e.key === ' ' && s.worker.state === 'walking' && !e.repeat) {
          s.worker.vy = JUMP_VELOCITY
          s.worker.state = 'jumping'
          audio.jump()
        }
      }
      if (e.key === 'p' || e.key === 'P') {
        if (s.status === 'playing') s.status = 'paused'
        else if (s.status === 'paused') s.status = 'playing'
        setHud(p => ({ ...p, status: s.status }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready' || cur === 'won') startGame()
        else if (cur === 'playing' || cur === 'paused') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }

    const onKeyUp = (e: KeyboardEvent): void => {
      keysRef.current[e.key] = false
      keysRef.current[e.key.toLowerCase()] = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const explode = (x: number, y: number, color: string, count = 10): void => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const speed = rand(1, 4)
        s.particles.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 35),
          color,
          size: rand(1.5, 3),
        })
      }
    }

    const showFloater = (x: number, y: number, text: string, color: string): void => {
      const s = stateRef.current
      if (!s) return
      s.floaters.push({ x, y, text, color, life: 60 })
    }

    const spawnContainer = (): void => {
      const s = stateRef.current
      if (!s) return
      // Containers spawn already on the topmost rolling platform (platform 4 =
      // PLATFORM_COUNT - 2). The TOP platform is flat — the crane and reach
      // stacker live there but containers never roll on it.
      const spawnP = s.platforms[PLATFORM_COUNT - 2]
      const startX = spawnP.rollDir > 0 ? spawnP.x1 + 4 : spawnP.x2 - 4
      const startY = interpY(spawnP, startX) - CONTAINER_H / 2
      const dropChance = 0.25 + (s.level - 1) * 0.05
      const dropAtLadders = new Set<Ladder>()
      for (const l of s.ladders) {
        if (Math.random() < dropChance) dropAtLadders.add(l)
      }
      s.containers.push({
        x: startX,
        y: startY,
        vx: 0,
        vy: 0,
        rollingOn: spawnP,
        lastPlatform: null,
        smashed: false,
        smashFrames: 0,
        colorIdx: randInt(0, 5),
        rotation: 0,
        speedMult: rand(0.85, 1.25),
        dropAtLadders,
        passedLadders: new Set<Ladder>(),
      })
      audio.drop()
      s.crane.dropFlash = 12
    }

    const killWorker = (): void => {
      const s = stateRef.current
      if (!s) return
      if (s.worker.state === 'dead') return
      s.worker.state = 'dead'
      s.worker.deathFrames = 90
      audio.hurt()
      s.shake = 10
      explode(s.worker.x, s.worker.y, '#ffd166', 18)
      s.lives--
    }

    const updateWorker = (s: GameState): void => {
      const w = s.worker
      const left = keysRef.current['ArrowLeft'] || keysRef.current['a']
      const right = keysRef.current['ArrowRight'] || keysRef.current['d']
      const up = keysRef.current['ArrowUp'] || keysRef.current['w']
      const down = keysRef.current['ArrowDown'] || keysRef.current['s']

      if (w.state === 'climbing') {
        const ladder = ladderAtX(s.ladders, w.x, w.y)
        if (!ladder) {
          w.state = 'walking'
        } else {
          w.x = ladder.x
          w.vx = 0
          if (up) {
            w.y -= CLIMB_SPEED
            w.climbAnim += 0.2
            if (s.time % 12 === 0) audio.climb(s.time % 24 < 12)
          } else if (down) {
            w.y += CLIMB_SPEED
            w.climbAnim += 0.2
            if (s.time % 12 === 0) audio.climb(s.time % 24 < 12)
          }
          if (w.y <= ladder.yTop) {
            w.y = ladder.yTop
            w.state = 'walking'
            const p = platformAtFeet(s.platforms, w.x, w.y, 4)
            if (p) w.onPlatform = p
          }
          if (w.y >= ladder.yBottom) {
            w.y = ladder.yBottom
            w.state = 'walking'
            const p = platformAtFeet(s.platforms, w.x, w.y, 4)
            if (p) w.onPlatform = p
          }
        }
      } else if (w.state === 'walking') {
        if (left) {
          w.vx = -WALK_SPEED
          w.facing = -1
          w.walkAnim += 0.25
        } else if (right) {
          w.vx = WALK_SPEED
          w.facing = 1
          w.walkAnim += 0.25
        } else {
          w.vx = 0
        }
        w.x += w.vx
        w.x = clamp(w.x, PLATFORM_LEFT_PAD + 4, W - PLATFORM_RIGHT_PAD - 4)

        const p = platformAtFeet(s.platforms, w.x, w.y, 12)
        if (p) {
          w.y = interpY(p, w.x)
          w.onPlatform = p
          w.vy = 0
        } else {
          w.state = 'jumping'
        }

        if (up) {
          const ladder = ladderAtX(s.ladders, w.x, w.y)
          if (ladder && Math.abs(w.y - ladder.yBottom) < 6) {
            w.state = 'climbing'
            w.x = ladder.x
          }
        }
        if (down) {
          const ladder = ladderAtX(s.ladders, w.x, w.y)
          if (ladder && Math.abs(w.y - ladder.yTop) < 6) {
            w.state = 'climbing'
            w.x = ladder.x
            w.y = ladder.yTop + 1
          }
        }
      } else if (w.state === 'jumping') {
        if (left) {
          w.vx = Math.max(w.vx - 0.3, -WALK_SPEED)
          w.facing = -1
        } else if (right) {
          w.vx = Math.min(w.vx + 0.3, WALK_SPEED)
          w.facing = 1
        }
        w.x += w.vx
        w.x = clamp(w.x, PLATFORM_LEFT_PAD + 4, W - PLATFORM_RIGHT_PAD - 4)
        w.vy += GRAVITY
        if (w.vy > MAX_FALL_SPEED) w.vy = MAX_FALL_SPEED
        w.y += w.vy
        const p = platformAtFeet(s.platforms, w.x, w.y, 8)
        if (p && w.vy > 0) {
          w.y = interpY(p, w.x)
          w.vy = 0
          w.state = 'walking'
          w.onPlatform = p
        }
        if (w.y > H + 50) {
          killWorker()
          return
        }
      }

      for (const c of s.containers) {
        if (c.smashed) continue
        if (
          Math.abs(c.x - w.x) < (CONTAINER_W + WORKER_W) / 2 - 2 &&
          Math.abs(c.y - w.y + WORKER_H / 2) < (CONTAINER_H + WORKER_H) / 2 - 4
        ) {
          if (w.powerupFrames > 0) {
            c.smashed = true
            c.smashFrames = 18
            s.score += SCORE_SMASH
            showFloater(c.x, c.y - 12, `+${SCORE_SMASH} SMASH`, '#d94a8f')
            audio.smash()
            explode(c.x, c.y, '#d94a4a', 12)
          } else {
            killWorker()
            return
          }
        }
      }

      // Powerup is a forklift attachment, not a heat shield — steam still kills.
      for (const jet of s.steamJets) {
        if (!steamJetActive(jet, s.time)) continue
        if (
          Math.abs(w.x - jet.x) < (STEAM_WIDTH + WORKER_W) / 2 &&
          w.y > jet.surfaceY - STEAM_HEIGHT &&
          w.y <= jet.surfaceY + 2
        ) {
          killWorker()
          return
        }
      }
    }

    const updateContainers = (s: GameState): void => {
      for (const c of s.containers) {
        if (c.smashed) {
          c.smashFrames--
          continue
        }
        if (c.rollingOn) {
          const p = c.rollingOn
          const speed = (CONTAINER_ROLL_SPEED + (s.level - 1) * 0.3) * c.speedMult
          const prevX = c.x
          c.x += speed * p.rollDir
          c.y = interpY(p, c.x) - CONTAINER_H / 2
          c.rotation += p.rollDir * 0.15

          for (const l of s.ladders) {
            if (c.passedLadders.has(l)) continue
            const crossed = (prevX < l.x && c.x >= l.x) || (prevX > l.x && c.x <= l.x)
            if (!crossed) continue
            const platformY = interpY(p, l.x)
            if (Math.abs(platformY - l.yBottom) > 8) continue
            c.passedLadders.add(l)
            if (c.dropAtLadders.has(l)) {
              c.x = l.x
              c.lastPlatform = c.rollingOn
              c.rollingOn = null
              c.vx = 0
              c.vy = 1
              break
            }
          }
          if (c.rollingOn) {
            if ((p.rollDir > 0 && c.x >= p.x2 - 2) || (p.rollDir < 0 && c.x <= p.x1 + 2)) {
              c.lastPlatform = c.rollingOn
              c.rollingOn = null
              c.vx = 0
              c.vy = 0
            }
          }
        } else {
          // Falling — swept platform check so we don't tunnel between frames.
          const prevBottomY = c.y + CONTAINER_H / 2
          c.vy += GRAVITY
          c.y += c.vy
          c.x += c.vx
          c.rotation += 0.2
          const newBottomY = c.y + CONTAINER_H / 2
          let landedOn: Platform | null = null
          let landedY = Infinity
          if (c.vy > 0) {
            for (const p of s.platforms) {
              if (p === c.lastPlatform) continue
              if (c.x < p.x1 || c.x > p.x2) continue
              const surfY = interpY(p, c.x)
              if (surfY >= prevBottomY - 1 && surfY <= newBottomY + 1) {
                if (surfY < landedY) {
                  landedY = surfY
                  landedOn = p
                }
              }
            }
          }
          if (landedOn) {
            c.y = landedY - CONTAINER_H / 2
            c.rollingOn = landedOn
            c.lastPlatform = null
            c.vx = 0
            c.vy = 0
            c.passedLadders = new Set<Ladder>()
          }
        }
      }
      s.containers = s.containers.filter(c => {
        if (c.smashed && c.smashFrames <= 0) return false
        return c.y < H + 60 && c.x > -40 && c.x < W + 40
      })
    }

    let animationId = 0

    const draw = (): void => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const scale = Math.min(screenW / W, screenH / H, 1.6)
      const offsetX = (screenW - W * scale) / 2
      const offsetY = (screenH - H * scale) / 2

      let shakeX = 0
      let shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      const sky = ctx.createLinearGradient(0, 0, 0, screenH)
      sky.addColorStop(0, '#0d1622')
      sky.addColorStop(0.5, '#1a2436')
      sky.addColorStop(1, '#080e18')
      ctx.fillStyle = sky
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      ctx.strokeStyle = 'rgba(60, 80, 110, 0.08)'
      ctx.lineWidth = 0.5
      for (let y = 0; y < H; y += 12) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(W, y)
        ctx.stroke()
      }

      if (s.status === 'playing') {
        if (s.worker.state !== 'dead') {
          updateWorker(s)
        } else {
          s.worker.deathFrames--
          if (s.worker.deathFrames <= 0) {
            if (s.lives < 0) {
              s.status = 'gameover'
              if (s.score > s.hi) {
                s.hi = s.score
                try {
                  localStorage.setItem('donkeyStackerHi', String(s.hi))
                } catch {
                  // localStorage may be unavailable; ignore.
                }
              }
              setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
            } else {
              respawnAfterDeath()
            }
          }
        }

        updateContainers(s)

        s.spawnTimer--
        if (s.spawnTimer <= 0) {
          spawnContainer()
          const minI = Math.max(40, CONTAINER_SPAWN_INTERVAL_MIN - (s.level - 1) * 15)
          const maxI = Math.max(80, CONTAINER_SPAWN_INTERVAL_MAX - (s.level - 1) * 25)
          s.spawnTimer = randInt(minI, maxI)
        }

        if (!s.powerup && s.worker.powerupFrames === 0 && Math.random() < 0.001) {
          const pIdx = randInt(1, PLATFORM_COUNT - 2)
          const p = s.platforms[pIdx]
          const px = (p.x1 + p.x2) / 2
          const py = interpY(p, px) - 12
          s.powerup = { x: px, y: py, life: 60 * 12 }
        }
        if (s.powerup) {
          const pu = s.powerup
          pu.life--
          if (
            s.worker.state !== 'dead' &&
            Math.abs(s.worker.x - pu.x) < 18 &&
            Math.abs(s.worker.y - pu.y) < 30
          ) {
            s.worker.powerupFrames = POWERUP_DURATION_FRAMES
            audio.powerup()
            showFloater(pu.x, pu.y - 14, 'FORKLIFT ATTACHMENT', '#d94a8f')
            s.powerup = null
          } else if (pu.life <= 0) {
            s.powerup = null
          }
        }
        if (s.worker.powerupFrames > 0) s.worker.powerupFrames--

        if (s.worker.state !== 'dead') {
          const onTop = !!s.worker.onPlatform && s.worker.onPlatform.i === PLATFORM_COUNT - 1
          if (onTop && Math.abs(s.worker.x - STACKER_X) < 30) {
            s.score += SCORE_REACH_TOP * s.level
            s.status = 'won'
            s.winFrames = 180
            audio.win()
            if (s.score > s.hi) {
              s.hi = s.score
              try {
                localStorage.setItem('donkeyStackerHi', String(s.hi))
              } catch {
                // localStorage may be unavailable; ignore.
              }
            }
            setHud(p => ({ ...p, status: 'won', score: s.score, hi: s.hi }))
          }
        }

        s.crane.armSwing += 0.06
        if (s.crane.dropFlash > 0) s.crane.dropFlash--

        for (const jet of s.steamJets) {
          const active = steamJetActive(jet, s.time)
          if (active && !jet.wasActive) audio.steam()
          jet.wasActive = active
        }

        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.2
          p.vx *= 0.96
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.5
          f.life--
          return f.life > 0
        })

        setHud(prev => {
          if (
            prev.score === s.score &&
            prev.lives === s.lives &&
            prev.level === s.level &&
            prev.status === s.status
          )
            return prev
          return { ...prev, score: s.score, lives: s.lives, level: s.level, status: s.status }
        })
      } else if (s.status === 'won') {
        s.winFrames--
        if (s.winFrames <= 0) {
          advanceLevel()
        }
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.2
          p.vx *= 0.96
          p.life--
          return p.life > 0
        })
      }

      drawCrane(ctx, s.crane, s.time)
      drawReachStacker(ctx, s.time)
      for (const p of s.platforms) drawPlatform(ctx, p)
      for (const l of s.ladders) drawLadder(ctx, l)
      for (const jet of s.steamJets) drawSteamJet(ctx, jet, s.time)
      for (const c of s.containers) drawContainer(ctx, c)
      if (s.powerup) drawPowerup(ctx, s.powerup, s.time)
      drawWorker(ctx, s.worker, s.time)

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 11px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      ctx.restore()

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SHIFT</span>
          <span style={styles.hudValue}>{String(hud.level).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>LIVES</span>
          <span style={styles.hudValue}>{'◉ '.repeat(Math.max(0, hud.lives))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            try {
              audioRef.current.init()
            } catch {
              // ignore audio init failures
            }
            if (startGameRef.current) startGameRef.current()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>DONKEY STACKER</div>
          <div style={styles.overlaySubLarge}>
            the gantry crane has gone rogue · climb the scaffolding · reach your reach stacker so
            you can finally start working
          </div>
          <div style={styles.controls}>
            <span style={{ color: '#cfd8e8' }}>
              ← → walk &nbsp;·&nbsp; ↑ ↓ climb &nbsp;·&nbsp; SPACE jump &nbsp;·&nbsp; P pause
            </span>
            <br />
            <span style={{ color: '#5a6478' }}>
              jump over rolling containers · grab the forklift attachment to smash through them
              <br />
              watch for broken steam pipes · don&apos;t fall off
            </span>
          </div>
          <div style={{ ...styles.controls, marginTop: 18 }}>press SPACE to start</div>
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>BREAK ROOM</div>
          <div style={styles.overlaySub}>press P to resume</div>
        </div>
      )}

      {hud.status === 'won' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>STACKER RECLAIMED</div>
          <div style={styles.overlaySub}>
            shift {hud.level} cleared · score: {hud.score.toLocaleString()}
          </div>
          <div style={styles.overlaySub}>next shift incoming...</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT TERMINATED</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
operator down at shift ${hud.level}
${hud.score.toLocaleString()} TEU credited
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default DonkeyStacker

// ============= DRAWING =============

function drawPlatform(ctx: CanvasRenderingContext2D, p: Platform): void {
  const dx = p.x2 - p.x1
  const dy = p.y2 - p.y1
  const len = Math.hypot(dx, dy)
  const nx = -dy / len
  const ny = dx / len
  const tx1 = p.x1
  const ty1 = p.y1
  const tx2 = p.x2
  const ty2 = p.y2
  const bx1 = p.x1 + nx * PLATFORM_HEIGHT
  const by1 = p.y1 + ny * PLATFORM_HEIGHT
  const bx2 = p.x2 + nx * PLATFORM_HEIGHT
  const by2 = p.y2 + ny * PLATFORM_HEIGHT

  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.beginPath()
  ctx.moveTo(tx1 + 2, ty1 + 2)
  ctx.lineTo(tx2 + 2, ty2 + 2)
  ctx.lineTo(bx2 + 2, by2 + 2)
  ctx.lineTo(bx1 + 2, by1 + 2)
  ctx.closePath()
  ctx.fill()

  ctx.fillStyle = '#c9a648'
  ctx.beginPath()
  ctx.moveTo(tx1, ty1)
  ctx.lineTo(tx2, ty2)
  ctx.lineTo(bx2, by2)
  ctx.lineTo(bx1, by1)
  ctx.closePath()
  ctx.fill()

  ctx.strokeStyle = 'rgba(255,255,255,0.3)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(tx1, ty1)
  ctx.lineTo(tx2, ty2)
  ctx.stroke()

  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(bx1, by1)
  ctx.lineTo(bx2, by2)
  ctx.stroke()

  ctx.strokeStyle = 'rgba(0,0,0,0.6)'
  ctx.lineWidth = 1.2
  const stepLen = 14
  const steps = Math.floor(len / stepLen)
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.4) / steps
    const cx = p.x1 + dx * t
    const cy = p.y1 + dy * t
    ctx.beginPath()
    ctx.moveTo(cx - (dx / len) * 2, cy - (dy / len) * 2)
    ctx.lineTo(cx + nx * (PLATFORM_HEIGHT - 2), cy + ny * (PLATFORM_HEIGHT - 2))
    ctx.lineTo(cx + (dx / len) * 2, cy + (dy / len) * 2)
    ctx.stroke()
  }
}

function drawLadder(ctx: CanvasRenderingContext2D, l: Ladder): void {
  const x = l.x
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(x - LADDER_W / 2, l.yTop)
  ctx.lineTo(x - LADDER_W / 2, l.yBottom)
  ctx.moveTo(x + LADDER_W / 2, l.yTop)
  ctx.lineTo(x + LADDER_W / 2, l.yBottom)
  ctx.stroke()
  ctx.strokeStyle = '#c9a648'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x - LADDER_W / 2, l.yTop)
  ctx.lineTo(x - LADDER_W / 2, l.yBottom)
  ctx.moveTo(x + LADDER_W / 2, l.yTop)
  ctx.lineTo(x + LADDER_W / 2, l.yBottom)
  ctx.stroke()
  const rungSpacing = 12
  for (let y = l.yTop + 4; y < l.yBottom - 2; y += rungSpacing) {
    ctx.strokeStyle = '#5a4520'
    ctx.lineWidth = 2.5
    ctx.beginPath()
    ctx.moveTo(x - LADDER_W / 2, y)
    ctx.lineTo(x + LADDER_W / 2, y)
    ctx.stroke()
    ctx.strokeStyle = '#c9a648'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x - LADDER_W / 2, y)
    ctx.lineTo(x + LADDER_W / 2, y)
    ctx.stroke()
  }
}

function drawSteamJet(ctx: CanvasRenderingContext2D, jet: SteamJet, time: number): void {
  const cx = jet.x
  const surfY = jet.surfaceY
  const active = steamJetActive(jet, time)
  const phase = steamJetPhase(jet, time)

  ctx.fillStyle = '#3a3a3a'
  ctx.fillRect(cx - 5, surfY - 4, 10, 4)
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(cx - 4, surfY - 3, 8, 1)
  ctx.fillStyle = 'rgba(150, 70, 30, 0.6)'
  ctx.fillRect(cx - 5, surfY - 1, 10, 1)
  ctx.fillStyle = '#0a0a0a'
  ctx.fillRect(cx - 3, surfY - 4, 6, 1)

  if (active) {
    const t = time * 0.4
    for (let i = 0; i < STEAM_HEIGHT; i++) {
      const yPos = surfY - 4 - i
      const wobble = Math.sin(t + i * 0.4) * 1.5
      const widthFactor = 1 - (i / STEAM_HEIGHT) * 0.4
      const w = STEAM_WIDTH * widthFactor
      const heightT = i / STEAM_HEIGHT
      const alpha = 0.85 - heightT * 0.5
      if ((i + Math.floor(t)) % 2 === 0) {
        ctx.fillStyle = `rgba(255, 240, 200, ${alpha})`
      } else {
        ctx.fillStyle = `rgba(255, 209, 102, ${alpha * 0.85})`
      }
      ctx.fillRect(cx - w / 2 + wobble, yPos, w, 1.5)
    }
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.fillRect(cx - 2, surfY - STEAM_HEIGHT, 4, STEAM_HEIGHT - 4)
    for (let i = 0; i < 3; i++) {
      const sx = cx + Math.sin(t * 1.3 + i * 2) * 6
      const sy = surfY - (i * 11 + ((time * 2) % 11))
      ctx.fillStyle = 'rgba(255, 209, 102, 0.7)'
      ctx.fillRect(sx, sy, 1.5, 1.5)
    }
  } else {
    if (phase > 0.7) {
      const warningT = (phase - 0.7) / 0.3
      ctx.fillStyle = `rgba(255, 240, 200, ${0.15 + warningT * 0.4})`
      const puffH = 3 + warningT * 8
      ctx.fillRect(cx - 3, surfY - 4 - puffH, 6, puffH)
      if (Math.floor(time / 4) % 2 === 0 && warningT > 0.5) {
        ctx.fillStyle = 'rgba(255, 209, 102, 0.8)'
        ctx.fillRect(cx - 4, surfY - 5, 8, 1)
      }
    }
  }
}

function drawContainer(ctx: CanvasRenderingContext2D, c: ContainerEntity): void {
  if (c.smashed) {
    const t = 1 - c.smashFrames / 18
    const palette = ['#d94a4a', '#3a8fd6', '#d97a2c', '#3aa15a']
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = palette[i % palette.length]
      const ox = Math.sin(i * 1.3) * 18 * t
      const oy = Math.cos(i * 0.9) * 14 * t
      ctx.globalAlpha = 1 - t
      ctx.fillRect(c.x + ox - 2, c.y + oy - 2, 4, 4)
    }
    ctx.globalAlpha = 1
    return
  }

  ctx.save()
  ctx.translate(c.x, c.y)
  const rotEffect = Math.sin(c.rotation) * 0.15
  ctx.rotate(rotEffect)

  const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72', '#e6c14a', '#d94a4a']
  const color = colors[c.colorIdx]

  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(-CONTAINER_W / 2 + 1, -CONTAINER_H / 2 + 1, CONTAINER_W, CONTAINER_H)

  ctx.fillStyle = color
  ctx.fillRect(-CONTAINER_W / 2, -CONTAINER_H / 2, CONTAINER_W, CONTAINER_H)
  ctx.fillStyle = 'rgba(255,255,255,0.22)'
  ctx.fillRect(-CONTAINER_W / 2, -CONTAINER_H / 2, CONTAINER_W, 1.5)
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(-CONTAINER_W / 2, CONTAINER_H / 2 - 1.5, CONTAINER_W, 1.5)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 5; i++) {
    const lx = -CONTAINER_W / 2 + (CONTAINER_W / 5) * i
    ctx.beginPath()
    ctx.moveTo(lx, -CONTAINER_H / 2 + 1)
    ctx.lineTo(lx, CONTAINER_H / 2 - 1)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 0.8
  ctx.strokeRect(-CONTAINER_W / 2, -CONTAINER_H / 2, CONTAINER_W, CONTAINER_H)

  ctx.restore()
}

function drawWorker(ctx: CanvasRenderingContext2D, w: Worker, time: number): void {
  if (w.state === 'dead') {
    const t = 1 - w.deathFrames / 90
    ctx.save()
    ctx.translate(w.x, w.y - WORKER_H / 2)
    ctx.rotate(t * Math.PI * 2)
    drawWorkerBody(ctx, w.facing, false, false, time)
    ctx.restore()
    return
  }

  ctx.save()
  ctx.translate(w.x, w.y - WORKER_H / 2)
  drawWorkerBody(
    ctx,
    w.facing,
    w.state === 'climbing',
    w.powerupFrames > 0,
    time,
    w.state === 'walking' ? w.walkAnim : 0,
    w.state === 'climbing' ? w.climbAnim : 0,
  )
  ctx.restore()
}

function drawWorkerBody(
  ctx: CanvasRenderingContext2D,
  facing: number,
  climbing: boolean,
  powered: boolean,
  time: number,
  walkAnim = 0,
  climbAnim = 0,
): void {
  const halfW = WORKER_W / 2
  const halfH = WORKER_H / 2

  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(-halfW + 1, halfH - 1, WORKER_W, 2)

  ctx.fillStyle = '#ffd166'
  ctx.fillRect(-halfW + 2, -halfH + 8, WORKER_W - 4, 10)
  ctx.fillStyle = 'rgba(255,255,255,0.6)'
  ctx.fillRect(-halfW + 3, -halfH + 11, WORKER_W - 6, 1)
  ctx.fillRect(-halfW + 3, -halfH + 15, WORKER_W - 6, 1)

  ctx.fillStyle = '#1a2236'
  const legPhase = climbing ? Math.sin(climbAnim * Math.PI) : Math.sin(walkAnim * Math.PI)
  const leftLegOffset = legPhase > 0 ? 1 : 0
  const rightLegOffset = legPhase > 0 ? 0 : 1
  ctx.fillRect(-halfW + 3, -halfH + 18, 4, 8 - leftLegOffset)
  ctx.fillRect(halfW - 7, -halfH + 18, 4, 8 - rightLegOffset)
  ctx.fillStyle = '#0a0a0a'
  ctx.fillRect(-halfW + 2, -halfH + 24 + leftLegOffset, 5, 2)
  ctx.fillRect(halfW - 7, -halfH + 24 + rightLegOffset, 5, 2)

  if (climbing) {
    ctx.fillStyle = '#ffd166'
    const armPhase = Math.sin(climbAnim * Math.PI)
    ctx.fillRect(-halfW + 1, -halfH + 6 + (armPhase > 0 ? -2 : 0), 3, 6)
    ctx.fillRect(halfW - 4, -halfH + 6 + (armPhase < 0 ? -2 : 0), 3, 6)
  } else {
    ctx.fillStyle = '#ffd166'
    const armOffset = Math.sin(walkAnim * Math.PI) * 1
    ctx.fillRect(-halfW + 1, -halfH + 9 + armOffset, 3, 6)
    ctx.fillRect(halfW - 4, -halfH + 9 - armOffset, 3, 6)
  }

  ctx.fillStyle = '#d4a574'
  ctx.fillRect(-halfW + 5, -halfH + 2, 6, 6)
  ctx.fillStyle = '#ffd166'
  ctx.fillRect(-halfW + 4, -halfH, 8, 4)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(-halfW + 3 + (facing > 0 ? 4 : 0), -halfH + 3, 4, 1)

  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(-halfW + (facing > 0 ? 9 : 5), -halfH + 4, 1, 1)

  if (powered) {
    const pulse = 0.5 + Math.sin(time * 0.3) * 0.3
    ctx.strokeStyle = `rgba(217, 74, 143, ${pulse})`
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.ellipse(0, 0, halfW + 4, halfH + 4, 0, 0, Math.PI * 2)
    ctx.stroke()
    ctx.fillStyle = '#5a5a5a'
    if (facing > 0) {
      ctx.fillRect(halfW, -halfH + 14, 6, 1.5)
      ctx.fillRect(halfW, -halfH + 18, 6, 1.5)
    } else {
      ctx.fillRect(-halfW - 6, -halfH + 14, 6, 1.5)
      ctx.fillRect(-halfW - 6, -halfH + 18, 6, 1.5)
    }
  }
}

function drawCrane(ctx: CanvasRenderingContext2D, crane: Crane, time: number): void {
  ctx.fillStyle = '#5a4520'
  ctx.fillRect(CRANE_X - 4, CRANE_Y, 8, 30)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(CRANE_X - 3, CRANE_Y, 6, 30)
  ctx.save()
  ctx.translate(CRANE_X, CRANE_Y)
  ctx.rotate(Math.sin(crane.armSwing) * 0.05)
  ctx.fillStyle = '#5a4520'
  ctx.fillRect(0, -6, 70, 8)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(0, -5, 70, 6)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.7
  for (let i = 0; i < 6; i++) {
    ctx.beginPath()
    ctx.moveTo(i * 12, -5)
    ctx.lineTo(i * 12 + 6, 1)
    ctx.moveTo(i * 12 + 6, -5)
    ctx.lineTo(i * 12, 1)
    ctx.stroke()
  }
  ctx.strokeStyle = '#7a7a7a'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(64, 1)
  ctx.lineTo(64, 14)
  ctx.stroke()
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(58, 14, 12, 4)
  ctx.fillStyle = 'rgba(0,0,0,0.5)'
  ctx.fillRect(58, 16, 12, 2)
  ctx.restore()

  ctx.fillStyle = '#3a2c12'
  ctx.fillRect(CRANE_X - 14, CRANE_Y + 28, 28, 14)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(CRANE_X - 12, CRANE_Y + 30, 24, 10)
  const beaconPulse = 0.3 + Math.sin(time * 0.2) * 0.7 + (crane.dropFlash > 0 ? 1 : 0)
  ctx.fillStyle = `rgba(220, 60, 60, ${Math.min(1, beaconPulse)})`
  ctx.fillRect(CRANE_X - 1, CRANE_Y + 26, 2, 2)
  ctx.fillStyle = crane.dropFlash > 0 ? '#ff6a4a' : '#1a2236'
  ctx.fillRect(CRANE_X - 8, CRANE_Y + 32, 16, 6)
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(CRANE_X - 7, CRANE_Y + 33, 14, 4)

  ctx.fillStyle = 'rgba(217, 74, 74, 0.7)'
  ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText('UNIT 001 — STATUS: ROGUE', CRANE_X, CRANE_Y - 8)
}

function drawReachStacker(ctx: CanvasRenderingContext2D, time: number): void {
  const cx = STACKER_X
  const baseY = PLATFORM_BOTTOM_Y - (PLATFORM_COUNT - 1) * PLATFORM_SPACING

  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(cx - 18, baseY - 1, 36, 2)

  const bodyGrad = ctx.createLinearGradient(0, baseY - 18, 0, baseY - 4)
  bodyGrad.addColorStop(0, '#e8b347')
  bodyGrad.addColorStop(0.5, '#c9943a')
  bodyGrad.addColorStop(1, '#8a6422')
  ctx.fillStyle = bodyGrad
  ctx.fillRect(cx - 16, baseY - 18, 32, 14)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(cx - 16, baseY - 6, 32, 2)
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 1
  ctx.strokeRect(cx - 16, baseY - 18, 32, 14)

  ctx.fillStyle = '#1a2236'
  ctx.beginPath()
  ctx.moveTo(cx - 8, baseY - 18)
  ctx.lineTo(cx - 4, baseY - 28)
  ctx.lineTo(cx + 8, baseY - 28)
  ctx.lineTo(cx + 8, baseY - 18)
  ctx.closePath()
  ctx.fill()
  const glassGrad = ctx.createLinearGradient(cx - 4, baseY - 28, cx + 8, baseY - 19)
  glassGrad.addColorStop(0, '#3a5a8a')
  glassGrad.addColorStop(1, '#1a2540')
  ctx.fillStyle = glassGrad
  ctx.beginPath()
  ctx.moveTo(cx - 7, baseY - 19)
  ctx.lineTo(cx - 4, baseY - 26)
  ctx.lineTo(cx + 6, baseY - 26)
  ctx.lineTo(cx + 6, baseY - 19)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(cx - 3, baseY - 25, 3, 1)

  ctx.fillStyle = '#5a4520'
  ctx.fillRect(cx - 22, baseY - 30, 4, 26)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(cx - 21, baseY - 30, 2, 26)
  ctx.fillStyle = '#3a3a3a'
  ctx.fillRect(cx - 24, baseY - 32, 8, 3)

  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(cx - 30, baseY - 8, 8, 1.5)
  ctx.fillRect(cx - 30, baseY - 4, 8, 1.5)

  ctx.fillStyle = '#0d0d0d'
  ctx.beginPath()
  ctx.arc(cx - 10, baseY - 2, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(cx + 10, baseY - 2, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#5a5a5a'
  ctx.beginPath()
  ctx.arc(cx - 10, baseY - 2, 1.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(cx + 10, baseY - 2, 1.2, 0, Math.PI * 2)
  ctx.fill()

  const beaconAlpha = 0.4 + Math.sin(time * 0.2) * 0.5
  ctx.fillStyle = `rgba(255, 209, 102, ${beaconAlpha})`
  ctx.fillRect(cx + 4, baseY - 30, 3, 3)
  ctx.fillStyle = `rgba(255, 209, 102, ${beaconAlpha * 0.3})`
  ctx.beginPath()
  ctx.arc(cx + 5.5, baseY - 28.5, 5, 0, Math.PI * 2)
  ctx.fill()

  if (Math.floor(time / 30) % 2 === 0) {
    ctx.fillStyle = 'rgba(255, 209, 102, 0.9)'
    ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('READY', cx, baseY - 36)
  }

  const pulse = 0.5 + Math.sin(time * 0.2) * 0.5
  ctx.fillStyle = `rgba(255, 209, 102, ${pulse})`
  ctx.font = 'bold 14px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText('!', cx, baseY - 48)
}

function drawPowerup(ctx: CanvasRenderingContext2D, p: Powerup, time: number): void {
  const pulse = 0.5 + Math.sin(time * 0.2) * 0.5
  ctx.fillStyle = `rgba(217, 74, 143, ${0.2 + pulse * 0.3})`
  ctx.beginPath()
  ctx.arc(p.x, p.y, 14, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(p.x - 6, p.y - 4, 12, 6)
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(p.x - 4, p.y + 2, 1.5, 6)
  ctx.fillRect(p.x + 2, p.y + 2, 1.5, 6)
  ctx.strokeStyle = '#d94a8f'
  ctx.lineWidth = 1.2
  ctx.strokeRect(p.x - 6, p.y - 4, 12, 6)
  ctx.fillStyle = '#d94a8f'
  ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText('FORKLIFT', p.x, p.y - 10)
  if (p.life < 60 && Math.floor(time / 6) % 2 === 0) {
    ctx.strokeStyle = '#ffd166'
    ctx.lineWidth = 1.5
    ctx.strokeRect(p.x - 8, p.y - 6, 16, 10)
  }
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 24,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 32,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: {
    fontSize: 13,
    letterSpacing: '0.22em',
    color: '#cfd8e8',
    marginBottom: 4,
    maxWidth: 460,
    textAlign: 'center',
  },
  controls: {
    marginTop: 8,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 420,
  },
}
